/**
 * "Креативы в кампании" — same look as the wizard's "Все креативы" modal.
 *
 * Two states inside the same centered modal:
 *   - LIST: 2-column grid of TelegramPost cards (= AllCreativesModal shape)
 *           with "Изменить" and "+ Добавить креатив" CTAs
 *   - EDITOR: the shared `<CreativeVariantsEditor>` from the wizard, working
 *           against the full variants array; "Сохранить" persists via
 *           PUT /api/v1/campaigns/{id}/creatives.
 *
 * The editor is the same component the wizard step 4 uses, so the form here
 * cannot drift from the wizard form.
 */
import { useState } from "react";
import { Pencil, Plus, X } from "lucide-react";
import { useCampaignCreatives, useReplaceCampaignCreatives, type CampaignCreativeVariant } from "@/shared/api/wb";
import { Button } from "@/shared/ui";
import { TelegramPost } from "../CampaignWizard/TelegramPost";
import { CreativeVariantsEditor, makeSeedVariant, SEED_VARIANT } from "../CampaignWizard/CreativeVariantsEditor";

type Mode = { kind: "list" } | { kind: "edit"; variants: CampaignCreativeVariant[] };

export function CreativesDrawer({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  const { data } = useCampaignCreatives(campaignId);
  const replace = useReplaceCampaignCreatives(campaignId);
  const [mode, setMode] = useState<Mode>({ kind: "list" });

  const existing = data?.variants ?? [];

  function openAdd() {
    const seed = existing.length === 0 ? { ...SEED_VARIANT } : makeSeedVariant(existing.length + 1);
    setMode({ kind: "edit", variants: [...existing, seed] });
  }

  function openEditAll() {
    setMode({ kind: "edit", variants: existing.length > 0 ? existing : [{ ...SEED_VARIANT }] });
  }

  async function save() {
    if (mode.kind !== "edit") return;
    await replace.mutateAsync(mode.variants);
    setMode({ kind: "list" });
  }

  const inEditor = mode.kind === "edit";

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center"
      style={{ background: "rgba(28,28,28,0.50)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[var(--bg)] rounded-lg shadow-lg fade-up flex flex-col max-h-[88vh] overflow-hidden"
        style={{ width: "min(1200px, 94vw)" }}>
        <div className="flex items-center justify-between p-6 border-b border-ink-12 flex-shrink-0">
          <div>
            <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40 mb-1">
              {inEditor ? "Редактирование" : "Креативы в кампании"}
            </div>
            <h3 className="font-display font-bold text-[20px] tracking-tight">
              {inEditor ? "Креативы" : `Все креативы · ${existing.length}`}
            </h3>
          </div>
          <div className="flex items-center gap-2.5">
            {inEditor ? (
              <>
                <Button size="sm" variant="outline" onClick={() => setMode({ kind: "list" })}>Отмена</Button>
                <Button size="sm" onClick={save} disabled={replace.isPending}>
                  {replace.isPending ? "Сохраняем…" : "Сохранить креатив"}
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={openAdd}>
                <Plus size={14} strokeWidth={3} /> Добавить креатив
              </Button>
            )}
            <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-md hover:bg-ink-6">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-6 flex-1">
          {inEditor && mode.kind === "edit" ? (
            <CreativeVariantsEditor
              variants={mode.variants}
              onChange={(vs) => setMode({ kind: "edit", variants: vs })}
            />
          ) : (
            <CreativesGrid variants={existing} onAdd={openAdd} onEditAll={openEditAll} />
          )}
        </div>
      </div>
    </div>
  );
}

function CreativesGrid({ variants, onAdd, onEditAll }: {
  variants: CampaignCreativeVariant[]; onAdd: () => void; onEditAll: () => void;
}) {
  if (variants.length === 0) {
    return (
      <div className="grid place-items-center text-center py-16 text-ink-40">
        <div className="text-[14px] mb-3">В этой РК пока нет креативов</div>
        <Button onClick={onAdd}>
          <Plus size={14} strokeWidth={3} /> Создать первый креатив
        </Button>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-x-5 gap-y-7">
      {variants.map((v) => (
        <div key={v.id} className="flex flex-col gap-2.5">
          <div className="flex items-center justify-between gap-2 px-1">
            <div className="font-display font-bold text-[15px]">{v.name}</div>
            <button onClick={onEditAll}
              className="inline-flex items-center gap-1 h-7 px-2.5 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40">
              <Pencil size={11} /> Изменить
            </button>
          </div>
          <TelegramPost p={{ name: v.name, text: v.text, media: v.media, link: v.link }} />
        </div>
      ))}
    </div>
  );
}
